import React, { useEffect, useMemo, useState } from 'react';
import { SectionDivider } from './SectionDivider';
import { Eye, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
const STORAGE_KEY_VIEWS = 'aiweb_pv_v4';
const STORAGE_KEY_VISITS = 'aiweb_visit_timestamps';
// Counter API — single endpoint, increment + read in one call
const PAGEVIEWS_UP = 'https://api.counterapi.dev/v1/aiweb-dublin-v4/views/up';
type TimeRange = 'hours' | 'days' | 'weeks' | 'month';
function getVisits(): number[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_VISITS);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
function recordVisit() {
  try {
    const visits = getVisits();
    visits.push(Date.now());
    const sixtyDaysAgo = Date.now() - 60 * 24 * 60 * 60 * 1000;
    const trimmed = visits.filter((t: number) => t > sixtyDaysAgo);
    localStorage.setItem(STORAGE_KEY_VISITS, JSON.stringify(trimmed));
  } catch {}
}
// Try fetching with retries for reliability
async function fetchWithRetry(url: string, retries = 2): Promise<number | null> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 4000);
      const res = await fetch(url, {
        signal: controller.signal
      });
      clearTimeout(timer);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const count = data.count ?? data.value ?? data.Count;
      if (typeof count === 'number') return count;
      throw new Error('Invalid response');
    } catch {
      if (attempt < retries) {
        await new Promise(r => setTimeout(r, 500 * (attempt + 1)));
      }
    }
  }
  return null; // All retries failed
}
async function incrementPageViews(): Promise<{
  count: number;
  synced: boolean;
}> {
  const result = await fetchWithRetry(PAGEVIEWS_UP);
  if (result !== null) {
    localStorage.setItem(STORAGE_KEY_VIEWS, String(result));
    return {
      count: result,
      synced: true
    };
  }
  // Fallback to local
  const local = parseInt(localStorage.getItem(STORAGE_KEY_VIEWS) || '0', 10) + 1;
  localStorage.setItem(STORAGE_KEY_VIEWS, String(local));
  return {
    count: local,
    synced: false
  };
}
function aggregateByHours(visits: number[]) {
  const now = Date.now();
  const data = [];
  for (let i = 23; i >= 0; i--) {
    const start = now - (i + 1) * 60 * 60 * 1000;
    const end = now - i * 60 * 60 * 1000;
    const count = visits.filter(t => t >= start && t < end).length;
    const hour = new Date(end).getHours();
    data.push({
      label: `${hour}:00`,
      visits: count
    });
  }
  return data;
}
function aggregateByDays(visits: number[]) {
  const now = Date.now();
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const data = [];
  for (let i = 6; i >= 0; i--) {
    const start = now - (i + 1) * 24 * 60 * 60 * 1000;
    const end = now - i * 24 * 60 * 60 * 1000;
    const count = visits.filter(t => t >= start && t < end).length;
    const dayName = days[new Date(end).getDay()];
    const date = new Date(end).getDate();
    data.push({
      label: `${dayName} ${date}`,
      visits: count
    });
  }
  return data;
}
function aggregateByWeeks(visits: number[]) {
  const now = Date.now();
  const data = [];
  for (let i = 3; i >= 0; i--) {
    const start = now - (i + 1) * 7 * 24 * 60 * 60 * 1000;
    const end = now - i * 7 * 24 * 60 * 60 * 1000;
    const count = visits.filter(t => t >= start && t < end).length;
    const weekStart = new Date(start);
    const weekEnd = new Date(end);
    const label = `${weekStart.getDate()}/${weekStart.getMonth() + 1} – ${weekEnd.getDate()}/${weekEnd.getMonth() + 1}`;
    data.push({
      label,
      visits: count
    });
  }
  return data;
}
function aggregateByMonth(visits: number[]) {
  const now = Date.now();
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const data = [];
  for (let i = 29; i >= 0; i--) {
    const start = now - (i + 1) * 24 * 60 * 60 * 1000;
    const end = now - i * 24 * 60 * 60 * 1000;
    const count = visits.filter(t => t >= start && t < end).length;
    const d = new Date(end);
    const label = `${d.getDate()} ${months[d.getMonth()]}`;
    data.push({
      label,
      visits: count
    });
  }
  return data;
}
function CustomTooltip({
  active,
  payload,
  label
}: any) {
  if (active && payload && payload.length) {
    return <div className="bg-[#111] border border-white/10 rounded-lg px-3 py-2 text-xs shadow-xl" data-id="element-426">
        <p className="text-white/60 mb-1" data-id="element-427">{label}</p>
        <p className="text-[#c9a84c] font-bold" data-id="element-428">
          {payload[0].value} visit{payload[0].value !== 1 ? 's' : ''}
        </p>
      </div>;
  }
  return null;
}
export function LocalSeoContent() {
  const [pageViews, setPageViews] = useState<number | null>(null);
  const [isSynced, setIsSynced] = useState(false);
  const [visits, setVisits] = useState<number[]>([]);
  const [timeRange, setTimeRange] = useState<TimeRange>('hours');
  useEffect(() => {
    recordVisit();
    setVisits(getVisits());
    incrementPageViews().then(({
      count,
      synced
    }) => {
      setPageViews(count);
      setIsSynced(synced);
    });
  }, []);
  const chartData = useMemo(() => {
    switch (timeRange) {
      case 'hours':
        return aggregateByHours(visits);
      case 'days':
        return aggregateByDays(visits);
      case 'weeks':
        return aggregateByWeeks(visits);
      case 'month':
        return aggregateByMonth(visits);
    }
  }, [visits, timeRange]);
  const tabs: {
    key: TimeRange;
    label: string;
  }[] = [{
    key: 'hours',
    label: '24 Hours'
  }, {
    key: 'days',
    label: '7 Days'
  }, {
    key: 'weeks',
    label: '4 Weeks'
  }, {
    key: 'month',
    label: '30 Days'
  }];
  return <section className="py-12 px-4 md:px-6 bg-[#050505]" data-id="element-429">
      <div className="max-w-4xl mx-auto reveal" data-id="element-430">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 text-center" data-id="element-431">
          Website Design For Dublin Businesses
        </h2>

        <div className="space-y-6 text-white/70 leading-relaxed text-lg" data-id="element-432">
          <p data-id="element-433">
            Are you a small business owner in Dublin looking for an affordable,
            professional website? At{' '}
            <span className="text-[#c9a84c] font-medium" data-id="element-434">ai.web</span>, we
            specialize in high-quality website design for Irish businesses that
            doesn't break the bank. Whether you're an electrician in Dublin 8, a
            restaurant in Temple Bar, or a local tradesperson serving the
            greater Dublin area, we build websites that help you get found
            online.
          </p>

          <p data-id="element-435">
            Most web design agencies in Ireland charge thousands of euros for a
            basic site. We've revolutionized the process by combining
            professional design with AI efficiency, allowing us to offer a
            complete
            <span className="text-white font-medium" data-id="element-436">
              {' '}
              Dublin Launch Package for just €249
            </span>
            . This isn't a DIY builder — it's a fully done-for-you service where
            we handle everything from design to launch.
          </p>

          <p data-id="element-437">
            Your package includes a mobile-responsive design, SEO-ready
            structure to help you rank on Google, and a{' '}
            <span className="text-[#c9a84c] font-medium" data-id="element-438">
              1-year MagicPatterns Hobby Plan (worth $180)
            </span>
            . This gives you the power to make edits yourself using simple AI
            commands, or let us handle the heavy lifting. Join the growing
            number of Dublin businesses upgrading their online presence today.
          </p>
        </div>

        {/* Visit Activity Chart */}
        <div className="mt-10 max-w-xl mx-auto" data-id="element-439">
          {/* Annotation arrow */}
          <div className="flex flex-col items-start mb-4 ml-2" data-id="element-440">
            <p className="text-white/60 text-sm italic whitespace-nowrap" data-id="element-441">
              This live view graph can be used in your website
            </p>
            <svg width="40" height="32" viewBox="0 0 40 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="ml-6 mt-1" data-id="element-442">
              <path d="M2 2C2 2 4 14 14 20C20 23.5 28 26 32 28" stroke="rgba(255,255,255,0.45)" strokeWidth="1.2" strokeLinecap="round" fill="none" data-id="element-443" />
              <path d="M28 24L32 28L27 29.5" stroke="rgba(255,255,255,0.45)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none" data-id="element-444" />
            </svg>
          </div>

          <div className="border border-white/[0.06] rounded-xl p-5 md:p-6" style={{
          backgroundColor: '#0a0a0a'
        }} data-id="element-445">
            {/* Header */}
            <div className="flex items-center justify-between mb-4" data-id="element-446">
              <div className="flex items-center space-x-3" data-id="element-447">
                <div className="bg-[#c9a84c]/10 p-2 rounded-full" data-id="element-448">
                  <Eye className="w-4 h-4 text-[#c9a84c]" data-id="element-449" />
                </div>
                <div data-id="element-450">
                  <p className="text-white font-bold text-sm" data-id="element-451">Site Activity</p>
                  <div className="flex items-center space-x-1.5" data-id="element-452">
                    {pageViews === null ? <div className="flex items-center space-x-1.5 text-white/40 text-xs" data-id="element-453">
                        <Loader2 className="w-3 h-3 animate-spin" data-id="element-454" />
                        <span data-id="element-455">Loading...</span>
                      </div> : <>
                        <p className="text-white/40 text-xs" data-id="element-456">
                          {pageViews.toLocaleString()} total page views
                        </p>
                        {isSynced && <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500" title="Synced across devices" data-id="element-457" />}
                      </>}
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex space-x-1 mb-4 bg-white/[0.03] rounded-lg p-1" data-id="element-458">
              {tabs.map(tab => <button key={tab.key} onClick={() => setTimeRange(tab.key)} className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-all ${timeRange === tab.key ? 'bg-[#c9a84c]/20 text-[#c9a84c]' : 'text-white/40 hover:text-white/60'}`} data-id="element-459">
                  {tab.label}
                </button>)}
            </div>

            {/* Chart */}
            <div className="h-40" data-id="element-460">
              <ResponsiveContainer width="100%" height="100%" data-id="element-461">
                <BarChart data={chartData} barCategoryGap="20%" data-id="element-462">
                  <XAxis dataKey="label" tick={{
                  fill: 'rgba(255,255,255,0.3)',
                  fontSize: 10
                }} axisLine={{
                  stroke: 'rgba(255,255,255,0.06)'
                }} tickLine={false} interval={timeRange === 'hours' ? 5 : timeRange === 'month' ? 4 : 0} data-id="element-463" />
                  <YAxis tick={{
                  fill: 'rgba(255,255,255,0.3)',
                  fontSize: 10
                }} axisLine={false} tickLine={false} allowDecimals={false} width={24} data-id="element-464" />
                  <Tooltip content={<CustomTooltip data-id="element-466" />} cursor={{
                  fill: 'rgba(201, 168, 76, 0.05)'
                }} data-id="element-465" />
                  <Bar dataKey="visits" radius={[3, 3, 0, 0]} maxBarSize={timeRange === 'weeks' ? 40 : timeRange === 'month' ? 14 : 20} data-id="element-467">
                    {chartData.map((entry, index) => <Cell key={index} fill={entry.visits > 0 ? '#c9a84c' : 'rgba(255,255,255,0.06)'} fillOpacity={entry.visits > 0 ? 0.8 : 1} data-id="element-468" />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <p className="text-center text-[10px] text-white/20 mt-3" data-id="element-469">
              {isSynced ? 'Live analytics • Synced across all devices' : 'Visit tracking • Per-browser activity'}
            </p>
          </div>
        </div>
      </div>
      <SectionDivider data-id="element-470" />
    </section>;
}